import CryptoJS from 'crypto-js';
// import heic2any from 'heic2any';
import { config } from './utils';

const decryptString = (value: string) => {
  let decryptedString = '';
  const dataString = value
    .replace(/p1L2u3S/g, '+')
    .replace(/s1L2a3S4h/g, '/')
    .replace(/e1Q2u3A4l/g, '=');
  try {
    decryptedString = CryptoJS.AES.decrypt(
      dataString,
      config.SECRET_KEY
    ).toString(CryptoJS.enc.Utf8);
  } catch (error) { }
  return decryptedString;
};

const encryptString = (value: string) => {
  const ciphertext = CryptoJS.AES.encrypt(
    String(value),
    config.SECRET_KEY
  ).toString();
  return ciphertext
    .replace(/\+/g, 'p1L2u3S')
    .replace(/\//g, 's1L2a3S4h')
    .replace(/=/g, 'e1Q2u3A4l');
};

const normalizeAssetUrl = (url: string) => {
  if (!url) return '';
  let normalized = url.trim();
  normalized = normalized.replace(/^https?:\/\/https\/\//i, 'https://');
  normalized = normalized.replace(/^https?:\/\/https:\/\//i, 'https://');
  return normalized;
};

/** API/storage urls that must not be used as img src (broken or not public). */
const isMalformedOrPrivateStorageUrl = (url: string) =>
  /^https?:\/\/https/i.test(url) ||
  /r2\.cloudflarestorage\.com/i.test(url);

const isHttpUrl = (value: string) =>
  value.startsWith('http://') ||
  value.startsWith('https://') ||
  value.startsWith('http');

/** Local/stage — serve files via API proxy (R2/CDN URLs often 404). Production uses CDN. */
const shouldUseAuthenticatedAssetProxy = (): boolean => {
  const apiBase = (config.BASE_URL || '').toLowerCase();
  if (
    apiBase.includes('localhost') ||
    apiBase.includes('127.0.0.1') ||
    apiBase.includes('apistag')
  ) {
    return true;
  }

  try {
    const host = (
      typeof window !== 'undefined' ? window.location.hostname : ''
    ).toLowerCase();
    return (
      host.includes('localhost') ||
      host.includes('appstage') ||
      host.includes('apistag')
    );
  } catch {
    return false;
  }
};

const buildAssetUrlFromKey = (key: string) => {
  if (!key) return '';
  if (isHttpUrl(key)) {
    return normalizeAssetUrl(key);
  }

  const base = process.env.REACT_APP_ASSET_URL || '';
  if (!base) return '';

  const path = key.startsWith('/') ? key.slice(1) : key;
  return path ? `${base.replace(/\/$/, '')}/${path}` : base;
};

const getAssetURl = (key: string, fallbackUrl?: string) => {
  if (!key && !fallbackUrl) return '';

  const fromKey = buildAssetUrlFromKey(key);
  if (fromKey) return fromKey;

  if (fallbackUrl) {
    const normalized = normalizeAssetUrl(fallbackUrl);
    if (!isMalformedOrPrivateStorageUrl(normalized)) {
      return normalized;
    }
  }

  return '';
};

const isImageMediaType = (fileType?: string, fileName?: string) => {
  if (!fileType) return false;

  const normalizedType = fileType.toLowerCase();
  if (normalizedType.startsWith('image/')) {
    return true;
  }

  if (
    normalizedType === 'application/octet-stream' &&
    fileName &&
    /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(fileName)
  ) {
    return true;
  }

  return false;
};

const isVideoMediaType = (fileType?: string) =>
  !!fileType && fileType.toLowerCase().startsWith('video/');

type AttachmentAsset = {
  id?: string;
  key?: string;
  url?: string;
  fileType?: string;
  fileName?: string;
};

const getApiBaseUrl = (): string =>
  config.BASE_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5050';

/** Asset id is embedded in storage key: `broker_license/{uuid}.png` */
const extractAssetIdFromKey = (key?: string): string | undefined => {
  if (!key) return undefined;
  const match = key.match(
    /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
  );
  return match?.[1];
};

const extractKeyFromAssetUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  try {
    const cdnBase = (process.env.REACT_APP_ASSET_URL || '').replace(/\/$/, '');
    const normalized = normalizeAssetUrl(url);
    if (cdnBase && normalized.startsWith(cdnBase)) {
      return normalized.slice(cdnBase.length + 1);
    }
    const parsed = new URL(normalized);
    return parsed.pathname.replace(/^\//, '');
  } catch {
    return undefined;
  }
};

const resolveAttachmentKey = (attachment: AttachmentAsset): string =>
  attachment.key || extractKeyFromAssetUrl(attachment.url) || '';

const shouldUseRelativeAssetProxy = (): boolean => {
  try {
    const host = (
      typeof window !== 'undefined' ? window.location.hostname : ''
    ).toLowerCase();
    return host === 'localhost' || host === '127.0.0.1';
  } catch {
    return false;
  }
};

const buildProxyAssetUrl = (assetId: string, key: string): string => {
  const path = `/upload-asset/${assetId}?key=${encodeURIComponent(key)}`;
  if (shouldUseRelativeAssetProxy()) {
    return path;
  }
  return `${getApiBaseUrl()}${path}`;
};

/**
 * Build asset URL via API upload-asset proxy (no authToken — anonymous GET).
 */
const getAuthenticatedAssetUrl = (
  asset: { id: string; key?: string; url?: string }
): string => {
  if (!asset?.key) return '';
  const assetId = asset.id || extractAssetIdFromKey(asset.key);
  if (!assetId) return '';
  return buildProxyAssetUrl(assetId, asset.key);
};

/**
 * Build asset URL for img src or links.
 * Local/stage uses API upload-asset proxy; production uses CDN when configured.
 * @param _accessToken Ignored — kept for call-site compatibility (authToken in URLs caused 401).
 */
const getAttachmentAssetUrl = (
  attachment?: AttachmentAsset | null,
  _accessToken?: string | null
) => {
  if (!attachment) return '';

  const key = resolveAttachmentKey(attachment);
  const assetId = attachment.id || extractAssetIdFromKey(key);
  const useProxy = shouldUseAuthenticatedAssetProxy();

  // Stage/local: never use CDN (private R2) — always API proxy when key is known
  if (useProxy && key && assetId) {
    return buildProxyAssetUrl(assetId, key);
  }

  const fromKey = buildAssetUrlFromKey(key);
  const normalizedUrl = attachment.url ? normalizeAssetUrl(attachment.url) : '';
  const assetBase = (process.env.REACT_APP_ASSET_URL || '').replace(/\/$/, '');

  // Local blob preview from the upload modal (create flow before publish)
  if (
    typeof attachment.url === 'string' &&
    attachment.url.startsWith('blob:')
  ) {
    return attachment.url;
  }

  if (fromKey && !useProxy) {
    return fromKey;
  }

  if (
    !useProxy &&
    normalizedUrl &&
    isHttpUrl(normalizedUrl) &&
    !isMalformedOrPrivateStorageUrl(normalizedUrl) &&
    (!assetBase || normalizedUrl.startsWith(assetBase))
  ) {
    return normalizedUrl;
  }

  if (key && assetId) {
    return buildProxyAssetUrl(assetId, key);
  }

  if (!useProxy && normalizedUrl && isHttpUrl(normalizedUrl)) {
    return normalizedUrl;
  }

  return '';
};

const getAttachmentPreviewSrc = (
  attachment?: AttachmentAsset | null,
  accessToken?: string | null
) => {
  if (!attachment) return '';

  if (isImageMediaType(attachment.fileType, attachment.fileName)) {
    return getAttachmentAssetUrl(attachment, accessToken);
  }

  if (isVideoMediaType(attachment.fileType)) {
    return '/static/video_placeholder.png';
  }

  return '/static/pdf.png';
};

export {
  encryptString,
  decryptString,
  getAssetURl,
  getAuthenticatedAssetUrl,
  getAttachmentAssetUrl,
  getAttachmentPreviewSrc,
  isImageMediaType,
  isVideoMediaType,
  normalizeAssetUrl,
};
